import type { FetchError } from 'ofetch';
import type { LocationQueryRaw, RouteLocationNormalized } from 'vue-router';
import { handleSessionExpiry } from '~/composables/useAuthenticatedFetch';
import { SubscriptionPurchasePlatform } from '~/enums/auth.enum';
import { SUPPORTED_LOCALES, type SupportedLocale } from '~/utils/locales';
import { buildLoginUrl } from '~/utils/loginRedirect';

export async function redirectMiddleware (to: RouteLocationNormalized) {
  const { checkAuthenticationStatus } = useAuthentication();
  const { $sentry } = useNuxtApp();
  const localePath = useLocalePath();

  const redirectToLogin = () => {
    clearError();

    return navigateTo(buildLoginUrl(to, localePath('/auth/login')), { replace: true });
  };

  const doRedirect = async (path: string, query?: LocationQueryRaw) => {
    clearError();

    return navigateTo({ path, query }, { redirectCode: 301 });
  };

  const doExternalRedirect = (url: string) => {
    clearError();

    return navigateTo(url, { external: true, replace: true });
  };

  const segments = to.path.split('/');
  const firstSegment = segments[1];
  let pathWithoutLocale = to.path;

  if (SUPPORTED_LOCALES.includes(firstSegment as SupportedLocale)) {
    const remainder = segments.slice(2).join('/');
    pathWithoutLocale = '/' + remainder;
  }

  if (pathWithoutLocale.length > 1 && pathWithoutLocale.endsWith('/')) {
    pathWithoutLocale = pathWithoutLocale.slice(0, -1);
  }

  if (pathWithoutLocale.startsWith('/enroll')) {
    const { user } = useUserStore();

    if (user.value?.subscription?.purchasePlatform) {
      const isPurchasePlatformApple = user.value.subscription.purchasePlatform === SubscriptionPurchasePlatform.APPLE;
      const isPurchasePlatformGoogle = user.value.subscription.purchasePlatform === SubscriptionPurchasePlatform.GOOGLE;

      if (isPurchasePlatformApple || isPurchasePlatformGoogle) {
        return doRedirect('/account/subscription', to.query);
      }
    }
  }

  if (pathWithoutLocale === '/' || to.path === '/') {
    return doRedirect('/dashboard', to.query);
  }

  const redirects = {
    '/course-library': '/courses',
    '/enroll': '/enroll/basic-monthly',
    '/enroll/pro': '/enroll/pro-yearly',
    '/user/login': '/auth/login',
    '/user/register': '/auth/signup',
    '/user/password': '/auth/forgot-password',
    '/auth/logout': '/auth/login',
    '/login': '/auth/login',
    '/account/subscription/edit-payment-method': '/account/subscription',
  };

  if (Object.keys(redirects).includes(pathWithoutLocale)) {
    const redirectPath = redirects[pathWithoutLocale as keyof typeof redirects];
    return doRedirect(redirectPath, to.query);
  }

  if (pathWithoutLocale === '/group' || pathWithoutLocale.startsWith('/group/')) {
    return doRedirect('/dashboard', to.query);
  }

  if (pathWithoutLocale.startsWith('/system/files/')) {
    const { createMedmasteryLink } = useCreateLink();

    return doExternalRedirect(createMedmasteryLink('/'));
  }

  // If no page is found, check if there is a redirect
  if (to.matched.length === 0) {
    // The redirect lookup needs a session, so logged-out visitors log in first and come back to this path
    if (!await checkAuthenticationStatus()) {
      return redirectToLogin();
    }

    const url = useApiUrl('/redirects?source=' + encodeURIComponent(to.path));

    try {
      const redirectResponse = await useAuthenticatedFetchSimplified<{destination: string}>(url, { retry: false });

      if (redirectResponse.destination) {
        return doRedirect(redirectResponse.destination, to.query);
      }
    } catch (error) {
      // The store can still hold the user of an expired session, which the lookup answers with 401 or 419
      if (await handleSessionExpiry(error, null)) {
        return redirectToLogin();
      }

      const statusCode = (error as FetchError).statusCode;

      if (statusCode !== 404) {
        console.error(`[Redirects] Redirect lookup failed with status ${statusCode}`, error);
        $sentry?.captureException(error, { extra: { statusCode, path: to.path }});
      }
    }

    showError({ statusCode: 404, statusMessage: 'Page not found' });
  }
}

export default defineNuxtRouteMiddleware(redirectMiddleware);
